// The first-login guided tour for the new Insights answer UI. Built on driver.js (tiny
// spotlight/popover tour). The driver.css + brand overrides live in index.css (scoped to
// the `insight-tour` popover class).
//
// Popover placement: driver drops a side-popover to the BOTTOM of an element taller than
// the viewport, which slides off screen. Instead we position the popover ourselves — it
// hugs the right edge of the step's target, vertically centred on the part of the target
// that is actually on screen, clamped so it never leaves the viewport, with the arrow
// pointing back at it. The `.itp-follow` rule in index.css locks that position with
// !important (so driver can't override it) and adds a transition so it GLIDES between
// steps. The overlay/halo keeps driver's own animation.

import { driver, type Config, type Driver, type DriveStep } from 'driver.js';

// Resolve a data-tour selector to the LAST match — a session can hold several visual
// answers (each renders the same hooks); every step targets the MOST RECENT one (the
// answer the banner appeared for) rather than an older answer higher up the scroll.
const lastEl = (selector: string) => (): Element => {
  const els = document.querySelectorAll(selector);
  return (els[els.length - 1] ?? els[0]) as Element;
};

const INSIGHTS_TAB = '[data-tour="insights-tab"]';
const TEXT_ANALYSIS_TAB = '[data-tour="text-analysis-tab"]';
// Click the LAST matching tab (most recent answer) so it becomes the active/bold tab while
// the matching step is shown.
const clickLast = (selector: string): void => {
  const els = document.querySelectorAll<HTMLElement>(selector);
  els[els.length - 1]?.click();
};

// Step 1 introduces the tabs (halo on the toggle); steps 2 and 3 spotlight the whole answer
// (halo on the answer), step 2 with Insights selected, step 3 flipped to Text Analysis.
// Exported as plain selectors so the wiring is unit-testable even though the steps resolve
// them as last-match functions.
export const INSIGHT_TOUR_SELECTORS = [
  '[data-tour="insights-toggle"]',
  '[data-tour="insights-answer"]',
  '[data-tour="insights-answer"]',
] as const;

// The tab each step activates so the view under the popover matches what the step describes:
// step 1 overviews the tabs (Insights default), step 2 shows Text Analysis, step 3 Insights.
const STEP_SELECT_TAB = [INSIGHTS_TAB, TEXT_ANALYSIS_TAB, INSIGHTS_TAB];

const POPOVERS: NonNullable<DriveStep['popover']>[] = [
  {
    title: 'Two ways to read every answer',
    description:
      'Every response now comes as a visual Insights report or a full Text Analysis. Switch anytime.',
  },
  {
    title: 'Text Analysis — the full write-up',
    description: 'Perfect when you need the complete narrative prose and every supporting quote.',
  },
  {
    title: 'Insights — the visual report',
    description:
      'Engineered for fast reading. Look here for headline summaries, key metrics, and scannable charts.',
  },
];

// --- popover follow-positioning -------------------------------------------------------
const POPOVER_CLASS = 'insight-tour';
const FOLLOW_CLASS = 'itp-follow';
const HEADER_CLEARANCE = 68; // keep clear of the fixed chat header
const VIEWPORT_MARGIN = 8;
const SIDE_GAP = 12; // gap between the target's right edge and the popover
const POPOVER_MAX_WIDTH = 300;
const FALLBACK_POPOVER_H = 170;
const FALLBACK_POPOVER_W = POPOVER_MAX_WIDTH;
const ARROW_EDGE_INSET = 12; // min distance the arrow sits from either popover edge
const NO_ARROW_CLASS = 'itp-noarrow'; // hides the arrow when the popover can't sit beside the target
const INSTANT_CLASS = 'itp-instant'; // disables the glide transition for one frame (FLIP seeding)
const INIT_ATTR = 'data-itp-init'; // marks a popover node we've already positioned

let activeDriver: Driver | null = null;
let followSelector: string = INSIGHT_TOUR_SELECTORS[0];
let following = false; // debounce: collapse rapid Next/Back into one refresh+reposition
// Last placed position. driver.js RECREATES the popover DOM node on every step, so a CSS
// transition alone can't glide between steps (the new node has nothing to animate from). We
// FLIP: seed each fresh node at the previous node's position, then transition to the new one.
let lastTop: number | null = null;
let lastLeft: number | null = null;

// The answer's viewport position is anchored across the content steps so switching the view
// (Insights <-> Text Analysis — different heights) doesn't make the report jump when driver
// re-scrolls it. Captured before a step's tab switch, restored after the content + halo settle.
const ANSWER_SEL = '[data-tour="insights-answer"]';
let anchorTop: number | null = null;

const scrollParentOf = (el: HTMLElement | null): HTMLElement | null => {
  let n = el?.parentElement ?? null;
  while (n) {
    const oy = getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n;
    n = n.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? null;
};

// Nudge the scroll container so the answer is back at its anchored viewport position.
const restoreAnchor = (): void => {
  if (anchorTop === null) return;
  const el = lastEl(ANSWER_SEL)() as HTMLElement | null;
  const scroller = scrollParentOf(el);
  if (!el || !scroller) return;
  const delta = el.getBoundingClientRect().top - anchorTop;
  if (delta) scroller.scrollTop += delta;
};

const setPos = (pop: HTMLElement, top: number, left: number): void => {
  pop.style.setProperty('--itp-top', `${top}px`);
  pop.style.setProperty('--itp-left', `${left}px`);
};

// Place the popover beside the current step's target, centred on the on-screen part of it,
// clamped into the viewport, with the arrow pointing at that visible middle.
function positionFollow(): void {
  const pop = document.querySelector<HTMLElement>(`.driver-popover.${POPOVER_CLASS}`);
  const target = lastEl(followSelector)() as HTMLElement | null;
  if (!pop || !target) return;
  pop.classList.add(FOLLOW_CLASS);
  pop.style.maxWidth = `${POPOVER_MAX_WIDTH}px`;

  const r = target.getBoundingClientRect();
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const ph = pop.offsetHeight || FALLBACK_POPOVER_H;
  const pw = pop.offsetWidth || FALLBACK_POPOVER_W;

  const visibleTop = Math.max(r.top, HEADER_CLEARANCE + VIEWPORT_MARGIN);
  const visibleBottom = Math.max(visibleTop, Math.min(r.bottom, vh - VIEWPORT_MARGIN));
  const visibleMid = (visibleTop + visibleBottom) / 2;

  const top = Math.max(
    HEADER_CLEARANCE + VIEWPORT_MARGIN,
    Math.min(visibleMid - ph / 2, vh - ph - VIEWPORT_MARGIN),
  );
  let left = r.right + SIDE_GAP;
  if (left + pw > vw - VIEWPORT_MARGIN) left = Math.max(VIEWPORT_MARGIN, vw - pw - VIEWPORT_MARGIN);
  // The arrow points LEFT at the target; only valid when the popover sits to the right of it.
  // On a narrow viewport the clamp above can land it over/left of the target — hide the arrow then.
  pop.classList.toggle(NO_ARROW_CLASS, left < r.right);

  // FLIP: a never-positioned node starts at the previous step's spot (no transition), so the
  // subsequent move to (top,left) glides instead of snapping in from driver's default.
  if (!pop.hasAttribute(INIT_ATTR)) {
    pop.setAttribute(INIT_ATTR, '');
    if (lastTop !== null && lastLeft !== null) {
      pop.classList.add(INSTANT_CLASS);
      setPos(pop, lastTop, lastLeft);
      void pop.offsetHeight; // force reflow so the seed position is committed before we glide
      pop.classList.remove(INSTANT_CLASS);
    }
  }
  lastTop = top;
  lastLeft = left;

  setPos(pop, top, left);
  pop.style.setProperty(
    '--itp-arrow',
    `${Math.max(ARROW_EDGE_INSET, Math.min(visibleMid - top, ph - ARROW_EDGE_INSET))}px`,
  );
}

// On a tab switch the answer's height changes: let driver re-measure the halo, then re-place
// the popover (it glides to the new visible-middle via the CSS transition). Debounced via
// `following` so rapid Next/Back within the double-rAF window collapse into a single
// refresh+reposition instead of stacking redundant ones.
function refreshAndFollow(): void {
  if (following) return;
  following = true;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      try {
        activeDriver?.refresh();
      } catch {
        /* tour already closed */
      }
      restoreAnchor(); // keep the report from jumping when its height changed
      positionFollow();
      following = false;
    }),
  );
}

export const INSIGHT_TOUR_STEPS: NonNullable<Config['steps']> = INSIGHT_TOUR_SELECTORS.map(
  (selector, i) => ({
    element: lastEl(selector),
    popover: POPOVERS[i],
    onHighlightStarted: () => {
      followSelector = selector;
      // Anchor the report where it already sits (step 1 does the initial scroll; later steps
      // keep it put so the Insights<->Text height change can't make it jump).
      anchorTop = i > 0 ? (lastEl(ANSWER_SEL)() as HTMLElement | null)?.getBoundingClientRect().top ?? null : null;
      clickLast(STEP_SELECT_TAB[i]);
      refreshAndFollow();
    },
    onHighlighted: () => {
      restoreAnchor();
      positionFollow();
    },
  }),
);

// Start the tour; `onDone` fires once when it ends (finished, skipped, or closed).
export function startInsightTour(onDone?: () => void): Driver {
  followSelector = INSIGHT_TOUR_SELECTORS[0];
  lastTop = null; // no FLIP-seed on the first step
  lastLeft = null;
  following = false; // clear a stale debounce from a tour torn down mid-rAF
  anchorTop = null; // step 1 scrolls freely; anchoring starts from step 2
  const d = driver({
    showProgress: true,
    progressText: '{{current}} of {{total}}',
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Got it',
    overlayOpacity: 0.55,
    stagePadding: 6,
    stageRadius: 10,
    popoverClass: POPOVER_CLASS,
    onPopoverRender: () => {
      restoreAnchor(); // undo driver's re-scroll before paint, so the report doesn't flash
      positionFollow(); // place it the instant it renders (before paint)
    },
    steps: INSIGHT_TOUR_STEPS,
    onDestroyed: () => {
      activeDriver = null;
      clickLast(INSIGHTS_TAB); // restore Insights if the tour was closed on Text Analysis
      onDone?.();
    },
  });
  activeDriver = d;
  d.drive();
  return d;
}
